import { ROTATION_DIRECTION } from "../../../multi-analysis/types";
import { unwrapPhaseDegrees } from "../../../scheme/components/analytics/utils/amplitude-phase";
import {
  ARROW_HEAD_ANGLE,
  ARROW_HEAD_LENGTH,
} from "../../../../../../components/common/chart-plugins/orbit-connecting-lines";

export interface INyquistChartSample {
  key: string;
  date: string;
  amplitude: number;
  phaseDegrees: number | null;
  rotationFrequencyRpm: number | null;
}

export interface INyquistChartSeries {
  key: string;
  pointId: number;
  measureId: number;
  name: string;
  pointName: string;
  measureName: string;
  unitCode: number;
  units: string;
  color: string;
  samples: readonly INyquistChartSample[];
}

export interface INyquistChartPoint {
  x: number;
  y: number;
  key: string;
  seriesKey: string;
  sample: INyquistChartSample;
}

export type NyquistChartKind = "cartesian" | "polar";

export interface INyquistLabelBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface INyquistRotationFrequencyLabel {
  key: string;
  x: number;
  y: number;
  text: string;
  width: number;
  color: string;
}

export interface INyquistPlacedRotationFrequencyLabel extends INyquistRotationFrequencyLabel, INyquistLabelBounds {
  textX: number;
  textY: number;
  leaderX: number;
  leaderY: number;
}

export const parseNyquistWallClock = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = "0", fraction = "0"] = match;
  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(fraction.padEnd(3, "0")),
  );
  const parsed = new Date(timestamp);
  return parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() === Number(month) - 1 &&
    parsed.getUTCDate() === Number(day) &&
    parsed.getUTCHours() === Number(hour) &&
    parsed.getUTCMinutes() === Number(minute) &&
    parsed.getUTCSeconds() === Number(second)
    ? timestamp
    : null;
};

export const formatNyquistWallClockTick = (date: string, includeDate = false): string | string[] => {
  const time = date.slice(11, 19);
  return includeDate ? [`${date.slice(8, 10)}.${date.slice(5, 7)}`, time.slice(0, 5)] : time;
};

export const orderNyquistSeries = (series: readonly INyquistChartSeries[]): INyquistChartSeries[] =>
  series.map(item => ({
    ...item,
    samples: item.samples
      .map(sample => ({ sample, time: parseNyquistWallClock(sample.date) }))
      .filter(entry => Number.isFinite(entry.sample.amplitude) && entry.time !== null)
      .sort((left, right) => left.time! - right.time!)
      .map(entry => entry.sample),
  }));

export const hasNyquistPhase = (sample: INyquistChartSample): boolean =>
  sample.phaseDegrees !== null && Number.isFinite(sample.phaseDegrees);

export const buildNyquistPoints = (
  series: INyquistChartSeries,
  kind: NyquistChartKind,
  phase = false,
): INyquistChartPoint[] => {
  const phases =
    kind === "cartesian" && phase
      ? unwrapPhaseDegrees(series.samples.map(sample => sample.phaseDegrees ?? Number.NaN))
      : [];
  return series.samples.map((sample, index) => {
    const validPhase = hasNyquistPhase(sample);
    const angle = ((sample.phaseDegrees ?? 0) * Math.PI) / 180;
    return {
      x:
        kind === "polar"
          ? validPhase
            ? sample.amplitude * Math.cos(angle)
            : Number.NaN
          : parseNyquistWallClock(sample.date)!,
      y:
        kind === "polar"
          ? validPhase
            ? sample.amplitude * Math.sin(angle)
            : Number.NaN
          : phase
            ? validPhase
              ? phases[index]
              : Number.NaN
            : sample.amplitude,
      key: sample.key,
      seriesKey: series.key,
      sample,
    };
  });
};

export const getNyquistArrowSegments = (
  points: readonly { x: number; y: number }[],
): { from: number; to: number }[] => {
  const segments: { from: number; to: number }[] = [];
  points.forEach((point, index) => {
    const previous = points[index - 1];
    if (
      previous &&
      Number.isFinite(previous.x) &&
      Number.isFinite(previous.y) &&
      Number.isFinite(point.x) &&
      Number.isFinite(point.y) &&
      (previous.x !== point.x || previous.y !== point.y)
    ) {
      segments.push({ from: index - 1, to: index });
    }
  });
  if (segments.length <= 3) return segments;
  return [segments[0], segments[Math.floor(segments.length / 2)], segments[segments.length - 1]];
};

export const getNyquistVisibleArrowSegments = (
  points: readonly { x: number; y: number }[],
  bounds: INyquistLabelBounds,
): { from: { x: number; y: number }; to: { x: number; y: number } }[] => {
  if (
    ![bounds.left, bounds.top, bounds.right, bounds.bottom].every(Number.isFinite) ||
    bounds.right <= bounds.left ||
    bounds.bottom <= bounds.top
  )
    return [];
  const segments: { from: { x: number; y: number }; to: { x: number; y: number } }[] = [];
  const clampX = (value: number) => Math.max(bounds.left, Math.min(bounds.right, value));
  const clampY = (value: number) => Math.max(bounds.top, Math.min(bounds.bottom, value));
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (![from.x, from.y, to.x, to.y, dx, dy].every(Number.isFinite) || (dx === 0 && dy === 0)) continue;
    let start = 0;
    let end = 1;
    let intersects = true;
    for (const [direction, distance] of [
      [-dx, from.x - bounds.left],
      [dx, bounds.right - from.x],
      [-dy, from.y - bounds.top],
      [dy, bounds.bottom - from.y],
    ]) {
      if (direction === 0) {
        if (distance < 0) intersects = false;
      } else if (direction < 0) start = Math.max(start, distance / direction);
      else end = Math.min(end, distance / direction);
      if (!intersects || start >= end) {
        intersects = false;
        break;
      }
    }
    if (!intersects) continue;
    const clippedFrom = {
      x: clampX(start === 0 ? from.x : from.x + dx * start),
      y: clampY(start === 0 ? from.y : from.y + dy * start),
    };
    const clippedTo = {
      x: clampX(end === 1 ? to.x : from.x + dx * end),
      y: clampY(end === 1 ? to.y : from.y + dy * end),
    };
    if (clippedFrom.x === clippedTo.x && clippedFrom.y === clippedTo.y) continue;
    segments.push({ from: clippedFrom, to: clippedTo });
  }
  if (segments.length <= 3) return segments;
  return [segments[0], segments[Math.floor(segments.length / 2)], segments[segments.length - 1]];
};

export const getNyquistArrowShape = (from: { x: number; y: number }, to: { x: number; y: number }) => {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (!Number.isFinite(length) || length === 0) return null;
  const x = from.x + (to.x - from.x) * 0.6;
  const y = from.y + (to.y - from.y) * 0.6;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  return {
    tip: { x, y },
    left: {
      x: x - ARROW_HEAD_LENGTH * Math.cos(angle - ARROW_HEAD_ANGLE),
      y: y - ARROW_HEAD_LENGTH * Math.sin(angle - ARROW_HEAD_ANGLE),
    },
    right: {
      x: x - ARROW_HEAD_LENGTH * Math.cos(angle + ARROW_HEAD_ANGLE),
      y: y - ARROW_HEAD_LENGTH * Math.sin(angle + ARROW_HEAD_ANGLE),
    },
  };
};

export const getNyquistPolarExtent = (series: readonly INyquistChartSeries[]): number => {
  const maximum = series.reduce(
    (extent, item) =>
      item.samples.reduce(
        (value, sample) =>
          hasNyquistPhase(sample) && Number.isFinite(sample.amplitude)
            ? Math.max(value, Math.abs(sample.amplitude))
            : value,
        extent,
      ),
    0,
  );
  if (maximum === 0) return 1;
  const step = 10 ** Math.floor(Math.log10(maximum));
  const extent = Math.ceil(maximum / step) * step;
  return Number.isFinite(extent) && extent > 0 ? extent : maximum;
};

export const getNyquistPolarBounds = (extent: number, width: number, height: number) => {
  const side = Math.max(1, Math.min(width, height));
  const radius = Number.isFinite(extent) && extent > 0 ? extent : 1;
  return {
    x: radius * (Math.max(1, width) / side),
    y: radius * (Math.max(1, height) / side),
  };
};

export const getNyquistRotationIndicator = (
  center: { x: number; y: number },
  radius: number,
  direction: ROTATION_DIRECTION,
  bounds: INyquistLabelBounds,
) => {
  if (direction === ROTATION_DIRECTION.UNDEFINED) return null;
  const start = (194 * Math.PI) / 180;
  const end = (224 * Math.PI) / 180;
  const arrowRadius = Math.min(
    Math.max(8, radius + 48),
    (center.x - bounds.left - 16) / Math.abs(Math.cos(start)),
    (center.y - bounds.top - 16) / Math.abs(Math.sin(end)),
  );
  if (!Number.isFinite(arrowRadius) || arrowRadius < 8) return null;
  const clockwise = direction === ROTATION_DIRECTION.CLOCKWISE;
  const headAngle = clockwise ? end : start;
  return {
    x: center.x,
    y: center.y,
    radius: arrowRadius,
    start: clockwise ? start : end,
    end: headAngle,
    anticlockwise: !clockwise,
    head: { x: center.x + Math.cos(headAngle) * arrowRadius, y: center.y + Math.sin(headAngle) * arrowRadius },
    headDirection: headAngle + (clockwise ? Math.PI / 2 : -Math.PI / 2),
  };
};

export const getNyquistRotationFrequencyPoints = (points: readonly INyquistChartPoint[]): INyquistChartPoint[] =>
  points.filter(
    point =>
      Number.isFinite(point.x) &&
      Number.isFinite(point.y) &&
      hasNyquistPhase(point.sample) &&
      point.sample.rotationFrequencyRpm !== null &&
      Number.isFinite(point.sample.rotationFrequencyRpm),
  );

export const layoutNyquistRotationFrequencyLabels = (
  labels: readonly INyquistRotationFrequencyLabel[],
  bounds: INyquistLabelBounds,
  obstacles: readonly INyquistLabelBounds[] = [],
  fontSize = 12,
): INyquistPlacedRotationFrequencyLabel[] => {
  const availableWidth = bounds.right - bounds.left;
  const availableHeight = bounds.bottom - bounds.top;
  if (availableWidth <= 0 || availableHeight <= 0) return [];
  const cellSize = 4;
  const columns = Math.ceil(availableWidth / cellSize);
  const rows = Math.ceil(availableHeight / cellSize);
  const occupied = new Float64Array(columns * rows);
  const totals = new Float64Array((columns + 1) * (rows + 1));
  const noFreeFits = new Set<string>();
  const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));
  const cells = (box: INyquistLabelBounds) => ({
    left: clamp(Math.floor((box.left - bounds.left) / cellSize), 0, columns),
    right: clamp(Math.ceil((box.right - bounds.left) / cellSize), 0, columns),
    top: clamp(Math.floor((box.top - bounds.top) / cellSize), 0, rows),
    bottom: clamp(Math.ceil((box.bottom - bounds.top) / cellSize), 0, rows),
  });
  const occupy = (box: INyquistLabelBounds, weight: number) => {
    const area = cells(box);
    for (let y = area.top; y < area.bottom; y += 1)
      for (let x = area.left; x < area.right; x += 1) occupied[y * columns + x] += weight;
  };
  const rebuildTotals = () => {
    for (let y = 1; y <= rows; y += 1) {
      let rowTotal = 0;
      for (let x = 1; x <= columns; x += 1) {
        rowTotal += occupied[(y - 1) * columns + x - 1];
        totals[y * (columns + 1) + x] = totals[(y - 1) * (columns + 1) + x] + rowTotal;
      }
    }
  };
  const collisions = (box: INyquistLabelBounds, useTotals: boolean) => {
    const area = cells(box);
    if (useTotals)
      return (
        totals[area.bottom * (columns + 1) + area.right] -
        totals[area.top * (columns + 1) + area.right] -
        totals[area.bottom * (columns + 1) + area.left] +
        totals[area.top * (columns + 1) + area.left]
      );
    let value = 0;
    for (let y = area.top; y < area.bottom; y += 1)
      for (let x = area.left; x < area.right; x += 1) value += occupied[y * columns + x];
    return value;
  };
  const obstacleKeys = new Set<string>();
  for (const box of [
    ...obstacles,
    ...labels.map(label => ({ left: label.x - 4, top: label.y - 4, right: label.x + 4, bottom: label.y + 4 })),
  ]) {
    const key = `${box.left}:${box.top}:${box.right}:${box.bottom}`;
    if (obstacleKeys.has(key)) continue;
    obstacleKeys.add(key);
    occupy(box, 1000);
  }
  return labels.map(label => {
    const width = Math.min(Math.max(1, label.width) + 8, availableWidth);
    const height = Math.min(fontSize + 4, availableHeight);
    const columnStep = Math.ceil(width / cellSize) * cellSize;
    const rowStep = Math.ceil(height / cellSize) * cellSize;
    const preferred = label.x <= (bounds.left + bounds.right) / 2 ? 1 : -1;
    const directions = [
      [preferred, 0],
      [preferred, -1],
      [preferred, 1],
      [-preferred, 0],
      [-preferred, -1],
      [-preferred, 1],
      [0, -1],
      [0, 1],
    ];
    let best: INyquistLabelBounds | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    let freeFitFound = false;
    const tried = new Set<string>();
    const consider = (candidateLeft: number, candidateTop: number, useTotals = false) => {
      const left = clamp(
        bounds.left + Math.round((candidateLeft - bounds.left) / columnStep) * columnStep,
        bounds.left,
        bounds.right - width,
      );
      const top = clamp(
        bounds.top + Math.round((candidateTop - bounds.top) / rowStep) * rowStep,
        bounds.top,
        bounds.bottom - height,
      );
      const location = `${left}:${top}`;
      if (tried.has(location)) return false;
      tried.add(location);
      const candidate = { left, top, right: left + width, bottom: top + height };
      const collisionScore = collisions(candidate, useTotals);
      const distance = Math.hypot(
        clamp(label.x, left, candidate.right) - label.x,
        clamp(label.y, top, candidate.bottom) - label.y,
      );
      const score = collisionScore + distance / (1 + Math.hypot(availableWidth, availableHeight));
      if (score < bestScore) {
        best = candidate;
        bestScore = score;
      }
      if (collisionScore === 0) freeFitFound = true;
      return collisionScore === 0;
    };
    for (let ring = 0; ring < 8; ring += 1) {
      const offset = 8 + ring * cellSize;
      for (const [dx, dy] of directions) {
        if (
          consider(
            dx > 0 ? label.x + offset : dx < 0 ? label.x - offset - width : label.x - width / 2,
            dy > 0 ? label.y + offset : dy < 0 ? label.y - offset - height : label.y - height / 2,
          )
        )
          break;
      }
      if (freeFitFound) break;
    }
    const fitKey = `${width}:${height}`;
    if (!freeFitFound && !noFreeFits.has(fitKey)) {
      rebuildTotals();
      const candidateColumns = Math.ceil((availableWidth - width) / columnStep);
      const candidateRows = Math.ceil((availableHeight - height) / rowStep);
      for (let column = 0; column <= candidateColumns; column += 1)
        for (let row = 0; row <= candidateRows; row += 1)
          consider(bounds.left + column * columnStep, bounds.top + row * rowStep, true);
      if (!freeFitFound) noFreeFits.add(fitKey);
    }
    const box = best!;
    occupy(box, 1);
    return {
      ...label,
      ...box,
      textX: box.left + 4,
      textY: box.top + Math.min(fontSize + 2, height - 1),
      leaderX: clamp(label.x, box.left, box.right),
      leaderY: clamp(label.y, box.top, box.bottom),
    };
  });
};

export const getNyquistKeyboardCursor = (
  series: readonly INyquistChartSeries[],
  cursorKey: string | null,
  key: string,
  kind: NyquistChartKind,
): string | null => {
  const active =
    series.find(
      item =>
        item.samples.some(sample => sample.key === cursorKey) &&
        item.samples.some(sample => kind === "cartesian" || hasNyquistPhase(sample)),
    ) ?? series.find(item => item.samples.some(sample => kind === "cartesian" || hasNyquistPhase(sample)));
  const samples = active?.samples ?? [];
  const indexes = samples.reduce<number[]>((result, sample, index) => {
    if (kind === "cartesian" || hasNyquistPhase(sample)) result.push(index);
    return result;
  }, []);
  if (!indexes.length) return null;
  const selectedIndex = samples.findIndex(sample => sample.key === cursorKey);
  const index = selectedIndex < 0 ? indexes[indexes.length - 1] : selectedIndex;
  switch (key) {
    case "ArrowLeft": {
      let previous = indexes[0];
      for (const candidate of indexes) {
        if (candidate >= index) break;
        previous = candidate;
      }
      return samples[previous].key;
    }
    case "ArrowRight":
      return samples[indexes.find(candidate => candidate > index) ?? indexes[indexes.length - 1]].key;
    case "Home":
      return samples[indexes[0]].key;
    case "End":
      return samples[indexes[indexes.length - 1]].key;
    default:
      return null;
  }
};

export const getNyquistTimeTicks = (
  series: readonly INyquistChartSeries[],
  limit: number,
): { value: number; date: string }[] => {
  const byTime = new Map<number, string>();
  series.forEach(item => item.samples.forEach(sample => byTime.set(parseNyquistWallClock(sample.date)!, sample.date)));
  const ticks = Array.from(byTime, ([value, date]) => ({ value, date })).sort(
    (left, right) => left.value - right.value,
  );
  if (ticks.length <= limit || limit < 2) return ticks.slice(0, Math.max(1, limit));
  const start = ticks[0].value;
  const duration = ticks[ticks.length - 1].value - start;
  const indexes = new Set<number>();
  for (let index = 0; index < limit; index += 1) {
    const target = start + (index / (limit - 1)) * duration;
    let best = 0;
    ticks.forEach((tick, position) => {
      if (Math.abs(tick.value - target) < Math.abs(ticks[best].value - target)) best = position;
    });
    indexes.add(best);
  }
  return ticks.filter((_tick, index) => indexes.has(index));
};
